import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Dashboard from "./pages/Dashboard";
import FleetPage from "./pages/FleetPage";
import JobsPage from "./pages/JobsPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import SensorCheckPage from "./pages/SensorCheckPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import DriverPage from "./pages/DriverPage";
import HistoryPage from "./pages/HistoryPage";
import InvoicingPage from "./pages/InvoicingPage";
import ImpoundsPage from "./pages/ImpoundsPage";
import ImpoundHistoryPage from "./pages/ImpoundHistoryPage";
import OwnedInLieuPage from "./pages/OwnedInLieuPage";
import YardStockPage from "./pages/YardStockPage";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import VersionWatcher from "./components/VersionWatcher";
import InstallPrompt from "./components/InstallPrompt";
import SettingsPage from "./pages/SettingsPage";
import PhotoShopPage from "./pages/PhotoShopPage";
import TowCalcPage from "./pages/TowCalcPage";
import OnboardingPage from "./pages/OnboardingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { AuthProvider, useAuth } from "./lib/auth";
import { hasCap } from "./lib/roles";
import { IntakeDialogProvider } from "./context/IntakeDialogContext";
import { api } from "./lib/api";
import { fetchBranding } from "./lib/company";
import { ImpersonationBanner } from "./components/ImpersonationBanner";

const APP_BG_STYLE = { backgroundColor: "#EEF1F5" };

// Feb 2026 — "PhotoShop" phone workflow: dispatchers who log in on a
// PHONE-SIZED VIEWPORT with a TOUCH-PRIMARY input get sent straight to
// the yard photo capture screen. The dashboard is unusable on phones
// (too dense), and per user's explicit choice there is NO escape hatch:
// once on /photoshop, a dispatcher on a phone STAYS there for the whole
// session. Admins on phones still get the dashboard (they're touching
// user/integration/setup flows that don't fit yard photos). Every
// non-`/photoshop` protected route bounces phone-dispatchers back.
function isPhoneDispatcher(user) {
  if (!user || user.role !== "dispatcher") return false;
  if (typeof window === "undefined") return false;
  const narrow = window.matchMedia("(max-width: 767px)").matches;
  const touchPrimary = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  return narrow && touchPrimary;
}

function NoAccessScreen({ pathname }) {
  const { user, logout } = useAuth();
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={APP_BG_STYLE}
      data-testid="no-access-screen"
    >
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">No access</h1>
        <p className="mt-3 text-sm text-slate-600">
          Your account{user?.email ? ` (${user.email})` : ""} isn&apos;t
          permitted to open <span className="font-mono">{pathname}</span>, and
          it has no default screen to fall back to.
        </p>
        <p className="mt-2 text-sm text-slate-600">
          Ask an admin to check your role, then sign in again.
        </p>
        <button
          type="button"
          onClick={logout}
          data-testid="no-access-sign-out"
          className="mt-6 w-full rounded-xl bg-slate-900 text-white py-3 text-sm font-medium hover:bg-slate-800 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function ProtectedShell({ children, allow = ["admin", "dispatcher"] }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  }
  // Trucks get bounced to /driver if they try to access dispatcher/admin routes
  if (user.role === "truck" && !allow.includes("truck")) {
    return <Navigate to="/driver" replace />;
  }
  // Phone-locked dispatcher: any non-photoshop route bounces back. No
  // escape hatch (per user's explicit "the dashboard is unusable on a
  // phone, don't bother"). See `isPhoneDispatcher` for the definition.
  if (loc.pathname !== "/photoshop" && isPhoneDispatcher(user)) {
    return <Navigate to="/photoshop" replace />;
  }
  // Oct 2026 — `hasCap` (not a bare role match) so `owner`, the only login
  // a brand-new tenant has, satisfies admin/dispatcher routes. A bare
  // includes() check sent owners into an infinite "/" → "/" redirect and
  // rendered a blank app.
  //
  // Jun 2026 — NEVER redirect to the path we're already on. A role that
  // can't open "/" used to self-redirect forever: no error, no sign-out,
  // just a white screen that survived every reload (a retired `viewer`
  // account hit exactly this in production). Show a way out instead.
  if (!hasCap(user, ...allow)) {
    if (loc.pathname === "/") return <NoAccessScreen pathname={loc.pathname} />;
    return <Navigate to="/" replace />;
  }
  return (
    <IntakeDialogProvider>
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <ImpersonationBanner />
          <Topbar />
          <main className="flex-1 overflow-auto" data-testid="main-content">
            {children}
          </main>
        </div>
        <VersionWatcher />
        <InstallPrompt />
      </div>
    </IntakeDialogProvider>
  );
}

function DriverShell({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  }
  // Dispatch/admin opening /driver should be sent back to dashboard
  if (user.role !== "truck") {
    return <Navigate to="/" replace />;
  }
  return <>{children}<VersionWatcher /><InstallPrompt /></>;
}

function PhotoShopShell({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  }
  if (!hasCap(user, "admin", "dispatcher")) {
    return <Navigate to="/" replace />;
  }
  // Full-screen, no sidebar/topbar chrome — this is a phone-first UI.
  return <>{children}<VersionWatcher /></>;
}

function AppRoutes() {
  useEffect(() => {
    api.get("/").catch(() => {});
    fetchBranding().then((b) => {
      if (b?.app_title) document.title = b.app_title;
    });
  }, []);

  return (
    <Routes>
      <Route path="/login" element={<><LoginPage /><InstallPrompt /></>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Diagnostic, no login: lets a driver's phone report its own sensors
          so the camera rotation fix can be designed against real readings
          instead of another assumption. Reads nothing, writes nothing. */}
      <Route path="/sensor-check" element={<SensorCheckPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/platform" element={<PlatformAdminPage />} />
      <Route
        path="/photoshop"
        element={
          <PhotoShopShell>
            <PhotoShopPage />
          </PhotoShopShell>
        }
      />
      <Route
        path="/driver"
        element={
          <DriverShell>
            <DriverPage />
          </DriverShell>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedShell>
            <Dashboard />
          </ProtectedShell>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedShell>
            <JobsPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/fleet"
        element={
          <ProtectedShell>
            <FleetPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedShell allow={["admin", "dispatcher"]}>
            <HistoryPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/impounds"
        element={
          <ProtectedShell allow={["admin", "dispatcher"]}>
            <ImpoundsPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/impound-history"
        element={
          <ProtectedShell allow={["admin", "dispatcher"]}>
            <ImpoundHistoryPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/owned-in-lieu"
        element={
          <ProtectedShell allow={["admin", "dispatcher"]}>
            <OwnedInLieuPage />
          </ProtectedShell>
        }
      />
      {/* Oct 2026 tenant-neutrality sweep: the route used to be /nts-impounds,
          which put one tenant's initials in everybody's address bar. Kept as a
          redirect because it will be bookmarked. */}
      <Route path="/nts-impounds" element={<Navigate to="/owned-in-lieu" replace />} />
      <Route
        path="/yards"
        element={
          <ProtectedShell allow={["admin", "dispatcher"]}>
            <YardStockPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedShell allow={["admin", "dispatcher"]}>
            <SettingsPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/invoicing"
        element={
          <ProtectedShell allow={["admin", "dispatcher"]}>
            <InvoicingPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/tow-calc"
        element={
          <ProtectedShell allow={["admin", "dispatcher"]}>
            <TowCalcPage />
          </ProtectedShell>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen" style={APP_BG_STYLE}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
